import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  forwardRef,
  inject,
  input,
  model,
  viewChild,
} from '@angular/core';
import {ControlValueAccessor, NG_VALUE_ACCESSOR} from '@angular/forms';
import {uniqId} from '@matchsource/utils';

// Use radio-button instead of this component. It provides support for radio-group

@Component({
  selector: 'ms-radio',
  templateUrl: './radio.component.html',
  styleUrls: ['./radio.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => RadioComponent),
      multi: true,
    },
  ],
  standalone: true,
  host: {
    '[attr.data-align]': 'align()',
  },
})
export class RadioComponent implements ControlValueAccessor {
  readonly value = input<any>(null);
  readonly name = input<string>();
  readonly isUncheckAllowed = input(false);
  readonly readOnly = input(false);
  readonly inputClasses = input<string>();
  readonly labelClasses = input<string>();
  readonly align = input<'left' | 'center'>('left');

  readonly disabled = model(false);

  readonly inputElement = viewChild.required<ElementRef<HTMLInputElement>>('input');

  private readonly cdRef = inject(ChangeDetectorRef);

  readonly inputId = uniqId('rad_');

  private onValueChange: (value: any) => Record<string, unknown>;

  private onTouch: any;

  private isChecked = false;

  registerOnChange(fn: any): void {
    this.onValueChange = fn;
  }

  registerOnTouched(fn: any): void {
    this.onTouch = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.disabled.set(isDisabled);
    this.cdRef.markForCheck();
  }

  writeValue(val: any): void {
    this.inputElement().nativeElement.checked = this.value() === val;
    this.cdRef.markForCheck();
  }

  blur() {
    if (this.onTouch) {
      this.onTouch();
    }
  }

  change() {
    let emitedValue = this.value();
    if (this.isUncheckAllowed() && this.isChecked) {
      this.inputElement().nativeElement.checked = false;
      emitedValue = null;
    }

    if (this.onValueChange) {
      this.onValueChange(emitedValue);
    }
  }

  click() {
    this.isChecked = this.inputElement().nativeElement.checked;
    if (this.isUncheckAllowed() && this.isChecked) {
      this.change();
    }
  }
}
